import Select from "react-select";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { api } from "../Assets/assetsShared";
import "./Timesheet.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// teal clock instead of the default purple
const tealTheme = createTheme({
  palette: { primary: { main: "#0f766e" } },
});

// make the picker field look like the other fields of this page
const fieldSx = {
  "& .MuiInputBase-input": {
    fontSize: 15,
    fontWeight: 500,
    color: "#0b2e2a",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": { borderColor: "#5fb8b0" },
    "&:hover fieldset": { borderColor: "#0f766e" },
    "&.Mui-focused fieldset": { borderColor: "#0f766e" },
  },
};

const pickerProps = {
  ampm: true,
  viewRenderers: {
    hours: renderTimeViewClock,
    minutes: renderTimeViewClock,
  },
  slotProps: {
    textField: { size: "small", fullWidth: true, sx: fieldSx },
  },
};

const today = () => new Date().toISOString().slice(0, 10);
const thisYear = () => String(new Date().getFullYear());
const thisMonth = () => String(new Date().getMonth() + 1).padStart(2, "0");

const EMPTY = {
  department: "",
  category: "",
  task: "",
  subTask: "",
  project: "",
  notes: "",
};

const uniq = (list) => Array.from(new Set(list));

// "09:30" (saved) -> dayjs, for the clock
const toDayjs = (value) => (value ? dayjs(`2000-01-01 ${value}`) : null);

// "09:30" -> "09:30 AM"
const showTime = (value) =>
  value ? dayjs(`2000-01-01 ${value}`).format("hh:mm A") : "";

// minutes between two times of the same day (null when a time is missing)
const minutesBetween = (start, end) => {
  if (!start || !end || !start.isValid() || !end.isValid()) return null;
  return end.hour() * 60 + end.minute() - (start.hour() * 60 + start.minute());
};

// 90 -> "1 hr 30 min"
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return "0 min";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h} hr` : "", m ? `${m} min` : ""].filter(Boolean).join(" ");
};

export default function Timesheet() {
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);

  // department chosen from the sidebar (/timesheet?department=Accounts)
  const lockedDept = searchParams.get("department") || "";

  const [master, setMaster] = useState([]);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [date, setDate] = useState(today());
  const [year, setYear] = useState(thisYear());
  const [month, setMonth] = useState(thisMonth()); // "01".."12" or "all"
  const [yearsWithData, setYearsWithData] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [editId, setEditId] = useState(null);
  // hours of an older entry that has no start/end time saved (null otherwise)
  const [editLegacyHours, setEditLegacyHours] = useState(null);
  const [message, setMessage] = useState("");

  // department used everywhere below: the sidebar one, or the dropdown one
  const activeDept = lockedDept || form.department;

  // time worked, calculated from start and end time
  const minutes = minutesBetween(startTime, endTime);
  const timeInvalid = minutes !== null && minutes <= 0;

  // older entry, no time chosen yet: show the saved duration
  const savedMinutes =
    editLegacyHours !== null && !startTime && !endTime
      ? Math.round(editLegacyHours * 60)
      : null;

  const totalText = timeInvalid
    ? "End must be after start"
    : minutes !== null
      ? formatDuration(minutes)
      : savedMinutes
        ? `${formatDuration(savedMinutes)} (saved)`
        : "0 min";

  const resetForm = () => {
    setForm(EMPTY);
    setStartTime(null);
    setEndTime(null);
    setEditId(null);
    setEditLegacyHours(null);
  };

  /* ---------- clear the form when another department is opened ---------- */
  useEffect(() => {
    resetForm();
    setMessage("");
  }, [lockedDept]);

  /* ---------- load master (Softvolute data only) ---------- */
  useEffect(() => {
    api
      .get("/timesheet-master")
      .then((res) => setMaster(res.data || []))
      .catch(() => setMessage("Could not load the timesheet master."));

    // Optional: project list. If this endpoint does not exist, a text box is shown.
    api
      .get("/projects")
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.projects || [];
        setProjects(
          list.map((p) => p.projectName || p.name || p.title).filter(Boolean),
        );
      })
      .catch(() => setProjects([]));
  }, []);

  /* ---------- years in which I have entries ---------- */
  const loadYears = () =>
    api
      .get("/timesheet/years")
      .then((res) => setYearsWithData(Array.isArray(res.data) ? res.data : []))
      .catch(() => setYearsWithData([]));

  useEffect(() => {
    loadYears();
  }, []);

  const yearOptions = useMemo(
    () =>
      uniq([...yearsWithData, thisYear(), year]).sort((a, b) =>
        b.localeCompare(a),
      ),
    [yearsWithData, year],
  );

  /* ---------- load my entries of the selected year ---------- */
  const loadEntries = () =>
    api
      .get("/timesheet", { params: { year } })
      .then((res) => setEntries(res.data || []))
      .catch(() => setMessage("Could not load your entries."));

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line
  }, [year]);

  /* ---------- changing the year: current month for this year, else all ---------- */
  const changeYear = (value) => {
    setYear(value);
    setMonth(value === thisYear() ? thisMonth() : "all");
  };

  /* ---------- picking a date moves the year and month with it ---------- */
  const changeDate = (value) => {
    setDate(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setYear(value.slice(0, 4));
      setMonth(value.slice(5, 7));
    }
  };

  /* ---------- cascading options ---------- */
  const departments = useMemo(
    () => uniq(master.map((m) => m.department)),
    [master],
  );

  const categories = useMemo(
    () =>
      uniq(
        master
          .filter((m) => m.department === activeDept)
          .map((m) => m.category || ""),
      ),
    [master, activeDept],
  );
  const hasCategories = categories.some(Boolean);

  const inScope = useMemo(
    () =>
      master.filter(
        (m) =>
          m.department === activeDept &&
          (!hasCategories || (m.category || "") === form.category),
      ),
    [master, activeDept, form.category, hasCategories],
  );

  const tasks = useMemo(() => uniq(inScope.map((m) => m.task)), [inScope]);

  const taskRows = inScope.filter((m) => m.task === form.task);
  const subTasks = uniq(taskRows.map((m) => m.subTask).filter(Boolean));
  const needsProject = taskRows.some((m) => m.requiresProject);

  const change = (field, value) =>
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "department") {
        next.category = "";
        next.task = "";
        next.subTask = "";
        next.project = "";
      }
      if (field === "category") {
        next.task = "";
        next.subTask = "";
        next.project = "";
      }
      if (field === "task") {
        next.subTask = "";
        next.project = "";
      }
      return next;
    });

  /* ---------- add or update ---------- */
  const saveEntry = async () => {
    if (!activeDept || !form.task) {
      setMessage("Select a department and task.");
      return;
    }
    if (hasCategories && !form.category) {
      setMessage("Select a category.");
      return;
    }
    if (subTasks.length && !form.subTask) {
      setMessage("Select a sub-task.");
      return;
    }
    if (needsProject && !form.project) {
      setMessage("Select a project.");
      return;
    }

    // older entry without times and no time chosen: keep the saved hours
    const keepSaved = editLegacyHours !== null && !startTime && !endTime;

    if (!keepSaved) {
      if (minutes === null) {
        setMessage("Select the start time and the end time.");
        return;
      }
      if (minutes <= 0) {
        setMessage("End time must be after the start time.");
        return;
      }
    }

    const payload = {
      ...form,
      department: activeDept,
      date,
      ...(keepSaved
        ? { hours: editLegacyHours }
        : {
            startTime: startTime.format("HH:mm"),
            endTime: endTime.format("HH:mm"),
            hours: Number((minutes / 60).toFixed(2)),
          }),
    };

    try {
      if (editId) {
        await api.put(`/timesheet/${editId}`, payload);
        setMessage("Entry updated.");
      } else {
        await api.post("/timesheet", payload);
        setMessage("Entry added.");
      }

      resetForm();

      // show the month of the saved date
      const entryYear = date.slice(0, 4);
      setMonth(date.slice(5, 7));
      if (entryYear !== year) setYear(entryYear);
      else loadEntries();

      loadYears();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not save the entry.");
    }
  };

  /* ---------- edit: fill the form with the entry ---------- */
  const startEdit = (entry) => {
    const hasTimes = Boolean(entry.startTime && entry.endTime);

    setEditId(entry._id);
    setDate(entry.date);
    setForm({
      department: entry.department || "",
      category: entry.category || "",
      task: entry.task || "",
      subTask: entry.subTask || "",
      project: entry.project || "",
      notes: entry.notes || "",
    });
    setStartTime(toDayjs(entry.startTime));
    setEndTime(toDayjs(entry.endTime));
    setEditLegacyHours(hasTimes ? null : Number(entry.hours || 0));

    setMessage(
      hasTimes
        ? "Editing the entry. Change what you need and click Update Entry."
        : `This older entry has no start and end time saved (${formatDuration(
            Math.round(Number(entry.hours || 0) * 60),
          )}). Pick the times to add them, or update the other details to keep the saved time.`,
    );

    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/timesheet/${id}`);
      if (editId === id) resetForm();
      loadEntries();
      loadYears();
    } catch (err) {
      setMessage("Could not delete the entry.");
    }
  };

  /* ---------- entries of this department (if one is selected) ---------- */
  const deptEntries = lockedDept
    ? entries.filter((e) => e.department === lockedDept)
    : entries;

  const monthOf = (e) => String(e.date || "").slice(5, 7);

  const countOfMonth = (m) =>
    deptEntries.filter((e) => monthOf(e) === m).length;

  /* ---------- months to show: only the ones that have entries ---------- */
  const monthOptions = useMemo(() => {
    const set = new Set(deptEntries.map((e) => monthOf(e)).filter(Boolean));

    // current month stays in the list for the current year
    if (year === thisYear()) set.add(thisMonth());

    // keep the month that is selected right now
    if (month !== "all") set.add(month);

    return Array.from(set).sort();
  }, [deptEntries, year, month]);

  /* ---------- entries of the selected month ---------- */
  const visibleEntries =
    month === "all"
      ? deptEntries
      : deptEntries.filter((e) => monthOf(e) === month);

  const total = visibleEntries.reduce(
    (sum, e) => sum + Number(e.hours || 0),
    0,
  );

  const monthLabel =
    month === "all" ? "All Months" : MONTH_NAMES[Number(month) - 1] || month;

  return (
    <div className="ts-page">
      {/* ----- banner ----- */}
      <header className="ts-banner">
        <div>
          <h1>Timesheet{lockedDept ? ` - ${lockedDept}` : ""}</h1>
          <p>Add your work hours and see the entries of any month.</p>
        </div>

        <div className="ts-stats">
          <div className="ts-stat">
            <strong>{monthLabel}</strong>
            <span>{year}</span>
          </div>
          <div className="ts-stat">
            <strong>{visibleEntries.length}</strong>
            <span>Entries</span>
          </div>
          <div className="ts-stat">
            <strong>{Number(total.toFixed(2))}</strong>
            <span>Total Hours</span>
          </div>
        </div>
      </header>

      {message && <div className="ts-message">{message}</div>}

      {/* ----- add / edit entry ----- */}
      <section className="ts-card" ref={formRef}>
        <div className="ts-card-head">
          <h3>{editId ? "Edit Entry" : "Add Entry"}</h3>
          {editId && <span className="ts-chip-edit">Editing</span>}
        </div>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={tealTheme}>
            <div className="ts-grid">
              {/* ----- time ----- */}
              <div className="ts-group-title">Time</div>

              <label className="ts-field">
                <span>Date</span>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => changeDate(e.target.value)}
                />
              </label>

              <div className="ts-field">
                <span>Start Time</span>
                <TimePicker
                  value={startTime}
                  onChange={(value) => setStartTime(value)}
                  {...pickerProps}
                />
              </div>

              <div className="ts-field">
                <span>End Time</span>
                <TimePicker
                  value={endTime}
                  onChange={(value) => setEndTime(value)}
                  {...pickerProps}
                />
              </div>

              <div className="ts-field">
                <span>Total Time</span>
                <div className={`ts-total ${timeInvalid ? "bad" : ""}`}>
                  {totalText}
                </div>
              </div>

              {/* ----- work details ----- */}
              <div className="ts-group-title">Work details</div>

              {!lockedDept && (
                <label className="ts-field">
                  <span>Department</span>
                  <select
                    value={form.department}
                    onChange={(e) => change("department", e.target.value)}
                  >
                    <option value="">Select department</option>
                    {departments.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {hasCategories && (
                <label className="ts-field">
                  <span>Category</span>
                  <select
                    value={form.category}
                    onChange={(e) => change("category", e.target.value)}
                  >
                    <option value="">Select category</option>
                    {categories.filter(Boolean).map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="ts-field">
                <span>Task</span>
                <select
                  value={form.task}
                  onChange={(e) => change("task", e.target.value)}
                  disabled={!activeDept || (hasCategories && !form.category)}
                >
                  <option value="">Select task</option>
                  {tasks.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>

              {subTasks.length > 0 && (
                <label className="ts-field">
                  <span>Sub-task</span>
                  <select
                    value={form.subTask}
                    onChange={(e) => change("subTask", e.target.value)}
                  >
                    <option value="">Select sub-task</option>
                    {subTasks.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {needsProject && (
                <div className="ts-field">
                  <span>Project</span>

                  {projects.length ? (
                    <Select
                      options={projects.map((p) => ({
                        value: p,
                        label: p,
                      }))}
                      value={
                        form.project
                          ? { value: form.project, label: form.project }
                          : null
                      }
                      onChange={(selected) =>
                        change("project", selected?.value || "")
                      }
                      placeholder="Search or select project..."
                      isSearchable
                      isClearable
                      noOptionsMessage={() => "No project found"}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        control: (base) => ({
                          ...base,
                          minHeight: 40,
                          borderRadius: 8,
                          borderColor: "#5fb8b0",
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  ) : (
                    <input
                      placeholder="Project name"
                      value={form.project}
                      onChange={(e) => change("project", e.target.value)}
                    />
                  )}
                </div>
              )}

              <label className="ts-field ts-notes">
                <span>Notes</span>
                <input
                  placeholder="Optional"
                  value={form.notes}
                  onChange={(e) => change("notes", e.target.value)}
                />
              </label>
            </div>
          </ThemeProvider>
        </LocalizationProvider>

        <div className="ts-form-footer">
          {editId && (
            <button
              className="ts-btn ts-btn-light"
              onClick={() => {
                resetForm();
                setMessage("");
              }}
            >
              Cancel
            </button>
          )}

          <button className="ts-btn" onClick={saveEntry}>
            {editId ? "Update Entry" : "Add Entry"}
          </button>
        </div>
      </section>

      {/* ----- entries ----- */}
      <section className="ts-card">
        <div className="ts-table-head">
          <h3>
            Entries for {monthLabel} {year}
            {lockedDept ? ` (${lockedDept})` : ""}
          </h3>

          <div className="ts-head-right">
            <label className="ts-year">
              <span>Year</span>
              <select value={year} onChange={(e) => changeYear(e.target.value)}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>

            <label className="ts-year">
              <span>Month</span>
              <select value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="all">All Months ({deptEntries.length})</option>
                {monthOptions.map((m) => (
                  <option key={m} value={m}>
                    {MONTH_NAMES[Number(m) - 1]} ({countOfMonth(m)})
                  </option>
                ))}
              </select>
            </label>

            <span className="ts-count">{Number(total.toFixed(2))} hrs</span>
          </div>
        </div>

        <div className="ts-table-wrap">
          <table className="ts-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Department</th>
                <th>Category</th>
                <th>Project</th>
                <th>Task</th>
                <th>Sub-task</th>
                <th>Hours</th>
                <th>Notes</th>
                <th className="ts-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.length === 0 ? (
                <tr>
                  <td colSpan={10} className="ts-empty">
                    No entries in {monthLabel} {year}.
                  </td>
                </tr>
              ) : (
                visibleEntries.map((e) => (
                  <tr
                    key={e._id}
                    className={e._id === editId ? "ts-row-editing" : ""}
                  >
                    <td className="ts-nowrap">{e.date}</td>
                    <td className="ts-nowrap">
                      {e.startTime && e.endTime ? (
                        <span className="ts-time">
                          {showTime(e.startTime)} - {showTime(e.endTime)}
                        </span>
                      ) : (
                        <span className="ts-muted">Not saved</span>
                      )}
                    </td>
                    <td>
                      <span className="ts-dept">{e.department}</span>
                    </td>
                    <td>{e.category || "-"}</td>
                    <td>{e.project || "-"}</td>
                    <td className="ts-strong">{e.task}</td>
                    <td>{e.subTask || "-"}</td>
                    <td>
                      <span className="ts-hours">{e.hours}</span>
                    </td>
                    <td className="ts-notes-cell" title={e.notes || ""}>
                      {e.notes || "-"}
                    </td>
                    <td className="ts-right ts-nowrap">
                      <button className="ts-edit" onClick={() => startEdit(e)}>
                        Edit
                      </button>
                      <button
                        className="ts-del"
                        onClick={() => removeEntry(e._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {visibleEntries.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={7}>
                    Total hours in {monthLabel} {year}
                  </td>
                  <td>
                    <span className="ts-hours ts-hours-total">
                      {Number(total.toFixed(2))}
                    </span>
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>
    </div>
  );
}
